import React, { useState, useEffect, useCallback } from "react";
import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { Table, Button, Form, Row, Col, Modal } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { selectUserDetails } from "../../redux/slices/userSlice";
import { fetchData, postData, putData, deleteData } from "../../redux/slices/apiCallSlice";
import { usePermissions } from "../../hooks/usePermissions";

function AdminUserPreferences() {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const userPreferencesUrl = apiUrls.userPreferences;
  const userDetails = useSelector(selectUserDetails);
  const { canView, canEdit, isLoading: isPermissionsLoading } = usePermissions();
  const canViewUserPreferences = canView("admin.user-preferences");
  const canEditUserPreferences = canEdit("admin.user-preferences");

  const [preferences, setPreferences] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPreference, setSelectedPreference] = useState(null);
  const [newPreference, setNewPreference] = useState({
    userObjectID: userDetails?.localAccountId || "",
    category: "",
    key: "",
    value: ""
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  useGuardedRealtimeList({ apiKey: 'userPreferences', apply: setPreferences, enabled: canViewUserPreferences, blocked: isLoading || showModal });

  // Function to fetch user preferences
  const fetchUserPreferences = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(fetchData({
        url: userPreferencesUrl
      })).unwrap();
      setPreferences(response);
    } catch (error) {
      console.error("Error fetching user preferences:", error);
      setErrorMessage("Failed to load user preferences. Please try again later.");
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setErrorMessage("");
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userPreferencesUrl]);

  // Fetch user preferences on component mount
  useEffect(() => {
    fetchUserPreferences();
  }, [fetchUserPreferences]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to handle opening the add modal
  const handleAddNew = () => {
    if (!canEditUserPreferences) {
      return;
    }

    setSelectedPreference(null);
    setNewPreference({
      userObjectID: userDetails?.localAccountId || "",
      category: "",
      key: "",
      value: ""
    });
    setShowModal(true);
  };

  // Function to handle opening the edit modal
  const handleEdit = (preference) => {
    if (!canEditUserPreferences) {
      return;
    }

    setSelectedPreference(preference);
    setShowModal(true);
  };

  // Function to handle modal input changes
  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    if (selectedPreference) {
      setSelectedPreference({
        ...selectedPreference,
        [name]: value
      });
    } else {
      setNewPreference({
        ...newPreference,
        [name]: value
      });
    }
  };

  // Function to handle saving changes (add or edit)
  const handleSave = async () => {
    if (!canEditUserPreferences) {
      return;
    }

    try {
      if (selectedPreference) {
        // Update existing preference
        await dispatch(putData({
          url: userPreferencesUrl,
          data: selectedPreference
        })).unwrap();
        setSuccessMessage("User preference updated successfully");
      } else {
        // Add new preference
        await dispatch(postData({
          url: userPreferencesUrl,
          data: newPreference
        })).unwrap();
        setSuccessMessage("User preference added successfully");
      }
      
      // Refresh preferences and close modal
      await fetchUserPreferences();
      setShowModal(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error saving user preference:", error);
      setErrorMessage(error.message || "Failed to save user preference");
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setErrorMessage("");
      }, 5000);
    }
  };

  // Function to handle deleting a preference
  const handleDelete = async (id) => {
    if (!canEditUserPreferences) {
      return;
    }

    if (window.confirm("Are you sure you want to delete this preference?")) {
      try {
        await dispatch(deleteData({
          url: userPreferencesUrl,
          deleteId: { ID: id }
        })).unwrap();
        
        // Refresh the data
        await fetchUserPreferences();
        setSuccessMessage("User preference deleted successfully");
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      } catch (error) {
        console.error("Error deleting user preference:", error);
        setErrorMessage(error.message || "Failed to delete user preference");
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setErrorMessage("");
        }, 5000);
      }
    }
  };

  // Function to close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setErrorMessage("");
  };

  // Render loading state
  if (isLoading || isPermissionsLoading) {
    return (
      <div className="m-4">
        <h2>User Preferences</h2>
        <div className="d-flex justify-content-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!canViewUserPreferences) {
    return <div className="alert alert-warning m-4">You do not have permission to view user preferences.</div>;
  }

  return (
    <div className="m-4">
      <h2>Manage User Preferences</h2>
      
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}
      {errorMessage && (
        <div className="alert alert-danger">{errorMessage}</div>
      )}

      {/* Add Button */}
      {canEditUserPreferences && (
        <Button 
          variant="primary" 
          onClick={handleAddNew} 
          className="mb-3"
        >
          Add New Preference
        </Button>
      )}

      {/* Preferences Table */}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>User ID</th>
            <th>Category</th>
            <th>Key</th>
            <th>Value</th>
            {canEditUserPreferences && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {preferences.map((preference) => (
            <tr key={preference.ID}>
              <td>{preference.ID}</td>
              <td>{preference.UserObjectID}</td>
              <td>{preference.Category}</td>
              <td>{preference.Key}</td>
              <td>{preference.Value}</td>
              {canEditUserPreferences && (
                <td>
                  <Button
                    variant="info"
                    size="sm"
                    onClick={() => handleEdit(preference)}
                    className="me-2"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(preference.ID)}
                  >
                    Delete
                  </Button>
                </td>
              )}
            </tr>
          ))}
          {preferences.length === 0 && (
            <tr>
              <td colSpan={canEditUserPreferences ? 6 : 5} className="text-center">
                No preferences found
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedPreference ? "Edit User Preference" : "Add New User Preference"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>User ID</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  name="userObjectID"
                  value={selectedPreference ? selectedPreference.userObjectID : newPreference.userObjectID}
                  onChange={handleModalInputChange}
                  required
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>Category</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  name="category"
                  value={selectedPreference ? selectedPreference.category : newPreference.category}
                  onChange={handleModalInputChange}
                  required
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>Key</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  name="key"
                  value={selectedPreference ? selectedPreference.key : newPreference.key}
                  onChange={handleModalInputChange}
                  required
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>Value</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  name="value"
                  value={selectedPreference ? selectedPreference.value : newPreference.value}
                  onChange={handleModalInputChange}
                  required
                />
              </Col>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!canEditUserPreferences}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminUserPreferences;
